interface Step1Data {
  eventType: string;
  eventSubtype: string;
}

interface Step1Props {
  data: Step1Data;
  onChange: (data: Partial<Step1Data>) => void;
  onNext: () => void;
}

const EVENT_TYPES = [
  {
    value: 'corporativo',
    label: 'Evento Corporativo',
    icon: 'ri-building-line',
    desc: 'Reuniões, treinamentos, convenções e conferências empresariais',
    subtypes: ['Convenção de Vendas', 'Treinamento Corporativo', 'Town Hall', 'Reunião de Diretoria', 'Workshop'],
  },
  {
    value: 'congresso',
    label: 'Congresso / Conferência',
    icon: 'ri-stethoscope-line',
    desc: 'Congressos médicos, científicos, acadêmicos e profissionais',
    subtypes: ['Congresso Médico', 'Conferência Científica', 'Simpósio', 'Seminário Acadêmico', 'Congresso de Especialidade'],
  },
  {
    value: 'feira',
    label: 'Feira e Exposição',
    icon: 'ri-store-3-line',
    desc: 'Feiras setoriais, exposições e eventos B2B',
    subtypes: ['Feira Industrial', 'Feira do Agronegócio', 'Exposição Tecnológica', 'Feira de Negócios', 'Evento B2B'],
  },
  {
    value: 'summit',
    label: 'Summit / Fórum',
    icon: 'ri-global-line',
    desc: 'Summits de inovação, fóruns de liderança e eventos de alto nível',
    subtypes: ['Summit de Tecnologia', 'Fórum de Liderança', 'Summit de Inovação', 'Evento Governamental', 'Conferência Diplomática'],
  },
  {
    value: 'multilingue',
    label: 'Evento Multilíngue',
    icon: 'ri-translate-2',
    desc: 'Eventos com tradução simultânea para múltiplos idiomas',
    subtypes: ['Conferência Internacional', 'Evento Diplomático', 'Cúpula Multilateral', 'Evento com Expositores Estrangeiros', 'Reunião Internacional'],
  },
  {
    value: 'outro',
    label: 'Outro Tipo',
    icon: 'ri-more-line',
    desc: 'Festival, evento cultural, esportivo ou outro formato',
    subtypes: ['Festival Cultural', 'Evento Esportivo', 'Show ou Espetáculo', 'Palestra Pública', 'Outro'],
  },
];

export default function Step1EventType({ data, onChange, onNext }: Step1Props) {
  const selected = EVENT_TYPES.find((e) => e.value === data.eventType);

  const handleTypeSelect = (value: string) => {
    onChange({ eventType: value, eventSubtype: '' });
  };

  const canProceed = data.eventType !== '' && data.eventSubtype !== '';

  return (
    <div>
      <div className="mb-8">
        <h2 className="font-heading font-bold text-3xl md:text-4xl text-secondary mb-2">
          Qual é o tipo do seu evento?
        </h2>
        <p className="font-body text-gray-500 text-base">
          Selecione a categoria que melhor descreve o evento para receber a proposta ideal.
        </p>
      </div>

      {/* Event type grid */}
      <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mb-8">
        {EVENT_TYPES.map((type) => (
          <button
            key={type.value}
            type="button"
            onClick={() => handleTypeSelect(type.value)}
            className={`relative text-left p-5 rounded-2xl border-2 transition-all duration-200 cursor-pointer hover:-translate-y-0.5 group ${
              data.eventType === type.value
                ? 'border-primary bg-primary/5'
                : 'border-gray-200 bg-white hover:border-primary/40 hover:bg-gray-50'
            }`}
          >
            <div
              className={`w-10 h-10 flex items-center justify-center rounded-xl mb-3 transition-colors duration-200 ${
                data.eventType === type.value ? 'bg-primary/15' : 'bg-gray-100 group-hover:bg-primary/10'
              }`}
            >
              <i
                className={`${type.icon} text-xl transition-colors duration-200 ${
                  data.eventType === type.value ? 'text-primary' : 'text-gray-500 group-hover:text-primary'
                }`}
              ></i>
            </div>
            <p
              className={`font-body font-semibold text-sm mb-1 transition-colors duration-200 ${
                data.eventType === type.value ? 'text-primary' : 'text-secondary'
              }`}
            >
              {type.label}
            </p>
            <p className="font-body text-xs text-gray-400 leading-relaxed hidden md:block">
              {type.desc}
            </p>
            {data.eventType === type.value && (
              <div className="absolute top-3 right-3 w-5 h-5 flex items-center justify-center rounded-full bg-primary">
                <i className="ri-check-line text-white text-xs"></i>
              </div>
            )}
          </button>
        ))}
      </div>

      {/* Subtype selection */}
      {selected && (
        <div className="mb-8 animate-fade-in">
          <label className="block font-body text-sm font-semibold text-secondary mb-3">
            Especifique o tipo de evento
          </label>
          <div className="flex flex-wrap gap-2">
            {selected.subtypes.map((sub) => (
              <button
                key={sub}
                type="button"
                onClick={() => onChange({ eventSubtype: sub })}
                className={`px-4 py-2 rounded-full text-sm font-body font-medium transition-all duration-200 cursor-pointer whitespace-nowrap ${
                  data.eventSubtype === sub
                    ? 'bg-primary text-white'
                    : 'bg-gray-100 text-gray-600 hover:bg-primary/10 hover:text-primary'
                }`}
              >
                {sub}
              </button>
            ))}
          </div>
        </div>
      )}

      <button
        type="button"
        onClick={onNext}
        disabled={!canProceed}
        className="w-full sm:w-auto inline-flex items-center justify-center gap-3 bg-primary text-white px-10 py-4 rounded-full font-body font-bold text-base uppercase tracking-wide hover:bg-opacity-90 transition-all hover:scale-105 whitespace-nowrap cursor-pointer disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:scale-100"
      >
        Continuar
        <div className="w-7 h-7 flex items-center justify-center bg-white/20 rounded-full">
          <i className="ri-arrow-right-line text-white"></i>
        </div>
      </button>
    </div>
  );
}